import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

// Store
import { RootState } from 'store';

// Saga helper
import {
	librariesRequest,
	searchRequest,
	searchRequestDrugs,
	searchRequestImpurities,
	searchRequestChemicalProbes
} from './saga-helper';

// Actions
import { hideLoader } from 'reducers/loader';
import * as actionsCreator from '../reducer';

// Lib
import { isEngLang } from 'lib/helpers';

// Type

function* _select<T>(fn: (state: RootState) => T) {
	const res: T = yield select(fn);
	return res;
}

export const search = (store: RootState) => store.search;

// Start Sagas
export function* searchTextSagaStart(api: any, action: any) {
	yield fork(searchTextDrugsWatcher, api);
	yield fork(searchTextCompoundWatcher, api);
	yield fork(searchTextLibrariesWatcher, api);
	yield fork(searchTextImpuritiesWatcher, api);
	yield fork(searchTextChemicalProbesWatcher, api);
}

/**
 *  search text compounds Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextCompoundWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchText.type,
		searchTextCompoundWorker,
		api
	);
}

/**
 *  search text libraries Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextLibrariesWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchText.type,
		searchTextLibrariesWorker,
		api
	);
}

/**
 *  search text drugs Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextDrugsWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchText.type,
		searchTextDrugsWorker,
		api
	);
}

/**
 *  search text impurities Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextImpuritiesWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchText.type,
		searchTextImpuritiesWorker,
		api
	);
}

/**
 *  search text impurities Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextChemicalProbesWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchText.type,
		searchTextChemicalProbesWorker,
		api
	);
}

/**
 *  Post search text for compounds Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextCompoundWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;
		const isHistory = action.payload.isHistory;
		const updateData = action.payload.type === 'update-data';

		const state = yield* _select(search);

		const type = state?.searchDataKeyBoard?.type;
		const size = state?.searchDataKeyBoard && type && (state.searchDataKeyBoard as Record<string, any>)[type]?.size;

		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextCompoundsLoadingTab());
			const formData = {
				value: value,
				size: state?.searchDataKeyBoard?.compounds?.size,
				page: !isHistory ? state?.searchDataKeyBoard?.compounds?.number : 0
			};

			const res = yield call(searchRequest, api, formData);

			if (!res.ok) {
				yield put(actionsCreator.searchTextError({
					type: 'error',
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					type: 'compounds',
					data: {
						...res.data,
						size: res.data.size === 0
							? size
							: res.data.size
					}
				};

				yield put(actionsCreator.searchTextCompoundsSuccess(formData.data));
				if (updateData) {
					yield put(actionsCreator.updateSearchTextData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextError({
				type: 'info',
				message: 'Please use Latin letters only'
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log('ERROR searchTextCompoundWorker', e);
	}
}

/**
 *  Post search text libraries Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextLibrariesWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;
		const isHistory = action.payload.isHistory;

		const state = yield* _select(search);

		const type = state?.searchDataKeyBoard?.type;
		const size = state?.searchDataKeyBoard && type && (state.searchDataKeyBoard as Record<string, any>)[type]?.size;

		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextLibrariesLoadingTab());
			const formData = {
				value: value,
				size: state?.searchDataKeyBoard?.libraries?.size,
				page: !isHistory ? state?.searchDataKeyBoard?.libraries?.number : 0
			};

			const res = yield call(librariesRequest, api, formData);

			if (!res.ok) {
				yield put(actionsCreator.searchTextError({
					type: 'error',
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					type: 'libraries',
					data: {
						...res.data,
						size: res.data.size === 0
							? size
							: res.data.size
					}
				};

				yield put(actionsCreator.searchTextLibrariesSuccess(formData.data));
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextError({
				type: 'info',
				message: 'Please use Latin letters only'
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log('ERROR searchTextLibrariesWorker', e);
	}
}

/**
 *  Post Search Text Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextDrugsWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;
		const isHistory = action.payload.isHistory;
		const updateData = action.payload.type === 'update-data';

		const state = yield* _select(search);

		const type = state?.searchDataKeyBoard?.type;
		const size = state?.searchDataKeyBoard && type && (state.searchDataKeyBoard as Record<string, any>)[type]?.size;

		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextDrugLoadingTab());
			const formData = {
				value: value,
				size: state?.searchDataKeyBoard?.drugs?.size,
				page: !isHistory ? state?.searchDataKeyBoard?.drugs?.number : 0
			};

			const res = yield call(searchRequestDrugs, api, formData);

			if (!res.ok) {
				yield put(actionsCreator.searchTextError({
					type: 'error',
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					type: 'drugs',
					data: {
						...res.data,
						size: res.data.size === 0
							? size
							: res.data.size
					}
				};

				yield put(actionsCreator.searchTextDrugSuccess(formData.data));
				if (updateData) {
					yield put(actionsCreator.updateSearchTextData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextError({
				type: 'info',
				message: 'Please use Latin letters only'
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log('ERROR searchTextDrugsWorker', e);
	}
}

/**
 *  Post Search Text Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextImpuritiesWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;
		const isHistory = action.payload.isHistory;
		const updateData = action.payload.type === 'update-data';

		const state = yield* _select(search);

		const type = state?.searchDataKeyBoard?.type;
		const size = state?.searchDataKeyBoard && type && (state.searchDataKeyBoard as Record<string, any>)[type]?.size;

		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextImpuritiesLoadingTab());
			const formData = {
				value: value,
				size: state?.searchDataKeyBoard?.impurities?.size,
				page: !isHistory ? state?.searchDataKeyBoard?.impurities?.number : 0
			};

			const res = yield call(searchRequestImpurities, api, formData);

			if (!res.ok) {
				yield put(actionsCreator.searchTextError({
					type: 'error',
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					type: 'impurities',
					data: {
						...res.data,
						size: res.data.size === 0
							? size
							: res.data.size
					}
				};

				yield put(actionsCreator.searchTextImpuritiesSuccess(formData.data));
				if (updateData) {
					yield put(actionsCreator.updateSearchTextData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextError({
				type: 'info',
				message: 'Please use Latin letters only'
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log('ERROR searchTextImpuritiesWorker', e);
	}
}

/**
 *  Post Search Text Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextChemicalProbesWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;
		const isHistory = action.payload.isHistory;
		const updateData = action.payload.type === 'update-data';

		const state = yield* _select(search);

		const type = state?.searchDataKeyBoard?.type;
		const size = state?.searchDataKeyBoard && type && (state.searchDataKeyBoard as Record<string, any>)[type]?.size;

		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextChemicalProbesLoadingTab());
			const formData = {
				value: value,
				size: state?.searchDataKeyBoard?.chemicalProbes?.size,
				page: !isHistory ? state?.searchDataKeyBoard?.chemicalProbes?.number : 0
			};

			const res = yield call(searchRequestChemicalProbes, api, formData);

			if (!res.ok) {
				yield put(actionsCreator.searchTextError({
					type: 'error',
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					type: 'chemicalProbes',
					data: {
						...res.data,
						size: res.data.size === 0
							? size
							: res.data.size
					}
				};

				yield put(actionsCreator.searchTextChemicalProbesSuccess(formData.data));
				if (updateData) {
					yield put(actionsCreator.updateSearchTextData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextError({
				type: 'info',
				message: 'Please use Latin letters only'
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log('ERROR searchTextChemicalProbesWorker', e);
	}
}
